import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, forkJoin, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { DashboardService } from '../../dashboard.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { ToastrService } from 'ngx-toastr';

import { DocumentTemplateService } from 'src/app/platform/nomenclatoare/document-template/document-template.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { DocumentHeaderFooterService } from 'src/app/platform/nomenclatoare/document-header-footer/document-header-footer.service';
import { DocumentHeader, DocumentFooter } from 'src/app/platform/nomenclatoare/document-header-footer/document-header-footer.interface';
@Component({
    selector: 'app-document-generabil',
    templateUrl: './document-generabil.component.html',
    styleUrls: ['./document-generabil.component.scss'],
})
export class DocumentGenerabilComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    token!: string;
    isLoading = false;
    uploading = false;

    helper = helper;
    safeHtml!: SafeHtml;

    // Header/Footer data
    availableHeaders: DocumentHeader[] = [];
    availableFooters: DocumentFooter[] = [];

    defaultChoiceParameter: { name: string; id: number } = {
        name: '-- Alegeți --',
        id: -1,
    };

    user: any;

    form: FormGroup;

    private destroy$ = new Subject<void>();

    templates: Generic[] = [];
    filteredTemplates: Generic[] = [];
    templatesControl = new FormControl();
    template: any;

    destination = 'toPrimarie';

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<DocumentGenerabilComponent>,
        private docService: DashboardService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private fb: FormBuilder,
        private storageService: StorageService,
        public toastr: ToastrService,
        private templateService: DocumentTemplateService,
        private headerFooterService: DocumentHeaderFooterService
    ) {
        this.processToken();
        this.form = this.fb.group({
            document_id: [this.data.document.id_document, Validators.required],
            template_id: [-1],
            context: ['', Validators.required],
            context_cetatean: [''],
            use_header: [true],
            use_footer: [true],
            header_id: [null],
            footer_id: [null],
        });
        console.log(data);
    }

    ngOnInit() {
        this.loadTemplates();
        this.loadHeadersAndFooters();
        this.renderFieldsSearch();
    }

    loadHeadersAndFooters(): void {
        forkJoin({
            headers: this.headerFooterService.getHeadersListActive(),
            footers: this.headerFooterService.getFootersListActive()
        }).subscribe({
            next: (response: any) => {
                this.availableHeaders = response.headers.data || [];
                this.availableFooters = response.footers.data || [];

                // Set default header/footer if available
                const defaultHeader = this.availableHeaders.find(h => h.is_default);
                const defaultFooter = this.availableFooters.find(f => f.is_default);

                if (defaultHeader) {
                    this.form.patchValue({ header_id: defaultHeader.id });
                }
                if (defaultFooter) {
                    this.form.patchValue({ footer_id: defaultFooter.id });
                }
            },
            error: (err) => {
                console.error('Error loading headers/footers:', err);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    processUserDetails() {
        var self = this;
        const userObj = self.storageService.getObject(environment.config.userKey);
        userObj
            .then((val) => {
                self.user = val;
            })
            .catch((err) => {
                console.log(err);
            });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token: string) => {
            this.token = token;
            this.docService.setToken(token);
            this.processUserDetails();
        });
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    renderFieldsSearch() {
        this.templatesControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTemplates = this.templates;
                    return of(this.templates);
                }
                return this.filterTemplates(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTemplates = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterTemplates(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.templates.filter(template =>
            this.normalizeString(template.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadTemplates() {
        this.isLoading = true;

        this.templateService
            .getRecordsList(this.data.is_address)
            .pipe(
                map((results: any) => [this.defaultChoiceParameter, ...results]),
                finalize(() => (this.isLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.templates = results;
                    this.filteredTemplates = [...this.templates];
                },
                error: (error) => {
                    console.error('Error loading templates', error);
                    this.filteredTemplates = [];
                },
            });
    }

    onTemplateClosed() {
        const selectedValue = this.templatesControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTemplates.some(template =>
                template.id === this.form.get('template_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.templatesControl.setValue(null);
                this.filteredTemplates = this.templates;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onTemplateChange(templateId: number) {
        if (templateId > 0) {
            this.getTemplateById(templateId);
            this.destination = 'toPrimarie';
        } else {
            this.template = null;
            this.form.patchValue({
                context: '',
                context_cetatean: '',
            });
        }
    }

    getTemplateById(templateId: number) {
        this.isLoading = true;
        this.templateService
            .templateByCerereId(templateId, this.data.document.id_document)
            .pipe(map((response: any) => response.data))
            .subscribe((requestData : any) => {
                console.log(requestData);
                this.isLoading = false;
                this.template = requestData;
                this.form.patchValue({
                    context: this.template.context,
                    context_cetatean: this.template.context_cetatean,
                });
            });
    }

    openToast(object: any) {
        const { type, message, title } = object;

        let options = this.toastr.toastrConfig;
        options.progressBar = true;
        options.preventDuplicates = true;
        options.closeButton = true;

        const inserted = this.toastr.show(
            message,
            title,
            options,
            options.iconClasses[type]
        );
    }

    onHttpError(res: any, showErrorsInToast: boolean = false) {
        if (res.errors && res.errors.message && res.errors.message.length > 0) {
            res.errors.message.forEach((msg: string) => {
                if (showErrorsInToast) {
                    this.openToast({
                        title: this.errorTitle,
                        message: msg,
                        type: this.errorType,
                    });
                } else {
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                }
            });
        } else {
            let errorMessage =
                environment.config.customNotifications.generalMessages.error;
            this.notificationService.warningSwal(
                this.errorTitle,
                errorMessage,
                this.errorIcon
            );
        }
    }

    submitDocumentGenerabil() {
        this.isLoading = true;

        let doc_type;
        if (this.template.document_generabil_dual == false) {
            doc_type = 1;
        } else {
            doc_type = 2;
        }
        this.docService
            .generateDocument(
                this.data.document.id_document,
                this.form.value.template_id,
                { ...this.form.value, ...this.data.is_address, doc_type: doc_type }
            )
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    onDestinatieChange(event: MatButtonToggleChange) {
        this.destination = event.value;
    }
}
